import React, { useState, useEffect } from 'react';
import AdminNavbar from './AdminNavbar';
import axios from 'axios';
import { Plus, FolderOpen, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { NewProjectModal } from './dashboard/NewProjectModal';
import { ProjectCard } from './dashboard/ProjectCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await axios.get(`${API}/website/projects`, { withCredentials: true });
        if (active) setProjects(response.data);
      } catch (error) {
        if (active) toast.error('Failed to load projects');
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/website/projects`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/website/projects/${projectId}`, { withCredentials: true });
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <AdminNavbar />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title & New Project Button */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-[#7dd3fc] font-bold text-3xl mb-2">Website Builder</h1>
            <p className="text-[#888680]">Create and manage your AI-generated websites</p>
          </div>
          <Button
            onClick={() => setShowNewProject(true)}
            data-testid="new-project-btn"
            className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-6 py-3 font-semibold flex items-center gap-2"
          >
            <Plus size={20} />
            New Project
          </Button>
        </div>

        {showNewProject && (
          <NewProjectModal onClose={() => setShowNewProject(false)} />
        )}

        {/* Projects Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-[#7dd3fc] animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 text-[#1e3a5f] mx-auto mb-4" />
            <h3 className="text-[#7dd3fc] font-bold text-xl mb-2">No Projects Yet</h3>
            <p className="text-[#888680] mb-6">Create your first AI-generated website</p>
            <Button
              onClick={() => setShowNewProject(true)}
              className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-6 py-3 font-semibold"
            >
              <Plus size={20} className="mr-2" />
              Create Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard
                key={project.project_id}
                project={project}
                onDelete={handleDeleteProject}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
